// Roll pills: one rounded pill per thing that makes a creature what it is
// (grade, level, talent, quality, bloodline, aura, trait, power), colored on
// the grade scale by how it compares with fresh wild catches. Hovering a pill
// shows its exact number, what it does and how rare it is. The per-part score
// breakdown lives in the calculator; these pills are the everyday view.
import { CONFIG } from '../shared/config.ts';
import { SPECIES } from '../shared/species.ts';
import { traitOf } from '../shared/traits.ts';
import { ivShare, STAT_NAMES } from '../shared/rolls.ts';
import { REFINE_STATS, refineOf, refineSpent } from '../shared/refine.ts';
import { TRAIN_STATS, trainOf, trainSpent } from '../shared/train.ts';
import { statWeights } from '../shared/rating.ts';
import { gradeOf, percentileBelow, type Grade } from '../shared/grade.ts';
import { creatureXpToNext, LEVEL_SLACK } from '../shared/xp.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { esc, full, summary, qualityEffect } from './fmt.ts';

export type Tier = 'c' | 'b' | 'a' | 's' | 'sp' | 'n' | 'gold' | 'rf' | 'tr' | 'medal';
const GRADE_TIER: Record<Grade, Tier> = { C: 'c', B: 'b', A: 'a', S: 's', 'S+': 'sp' };

/** The grade scale for a share of catches below: bottom half, better than most, 1 in 10, 1 in 100, 1 in 1,000. */
function tierFor(below: number): Tier {
  return below >= 99.9 ? 'sp' : below >= 99 ? 's' : below >= 90 ? 'a' : below >= 50 ? 'b' : 'c';
}
/** "Top 26% of wild catches" for the better half; nothing for the rest (the pill's color already says so). */
export function rankText(below: number) {
  if (below < 50) return '';
  const top = below >= 99.9 ? 0.1 : below >= 99 ? Math.max(0.1, Math.round((100 - below) * 10) / 10) : Math.ceil(100 - below);
  return `Top ${top}% of wild catches`;
}
const lines = (...xs: string[]) => xs.filter(Boolean).join('\n');
const SHORT = ['HP', 'Atk', 'Def', 'Mag', 'Res', 'Spd'];

// Weighted talent of fresh catches, per species (the weights differ): a fixed
// 20,000-sample table built the first time a species is shown.
const talentTables = new Map<string, Float64Array>();
function talentBelow(c: CreatureDTO): number {
  const key = c.species;
  let t = talentTables.get(key);
  if (!t) {
    const w = statWeights(c.species);
    t = new Float64Array(20_000);
    let seed = 0x9e3779b9;
    const rand = () => { seed = (Math.imul(seed ^ (seed >>> 15), 0x2c1b3c6d) + 0x6d2b79f5) >>> 0; return seed / 4294967296; };
    for (let i = 0; i < t.length; i++) {
      let v = 0;
      for (let k = 0; k < 6; k++) v += (Math.floor(rand() * 32) / 31) * w[k];
      t[i] = v;
    }
    t.sort();
    talentTables.set(key, t);
  }
  const x = ivShare(c.rolls, statWeights(c.species)) - 1e-9;
  let lo = 0, hi = t.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (t[m] < x) lo = m + 1; else hi = m; }
  return (lo / t.length) * 100;
}

/** Quality is the mean of three even draws (Irwin-Hall): the exact share of catches below. */
function qualityBelow(q: number): number {
  const { min, max, draws } = CONFIG.quality;
  const s = Math.min(draws, Math.max(0, ((q - min) / (max - min)) * draws - 1e-9));
  const cdf = s <= 1 ? s ** 3 / 6 : s <= 2 ? (-2 * s ** 3 + 9 * s ** 2 - 9 * s + 3) / 6 : 1 - (3 - s) ** 3 / 6;
  return cdf * 100;
}

const pill = (tier: Tier, label: string, value: string, tip: string, key = '') =>
  `<span class="rp t-${tier}"${key ? ` data-k="${key}"` : ''} tabindex="0" data-tip="${esc(tip)}">${label ? `<small>${label}</small>` : ''}${value}</span>`;

/** One roll as the pills show it: its name, its value, its tier on the grade scale and the hover text. */
export interface RollPart { key: 'grade' | 'level' | 'talent' | 'quality' | 'bloodline' | 'aura' | 'trait' | 'power'; label: string; value: string; short: string; tier: Tier; tip: string }

/** Every roll of a creature as pill parts, in pill order. Rolls never change after a catch. */
export function rollParts(c: CreatureDTO): RollPart[] {
  const sp = SPECIES[c.species];
  const s = summary(c);
  const g = gradeOf(s.rating);
  const out: RollPart[] = [];
  out.push({ key: 'grade', label: '', value: `${g} <i>${s.rating}</i>`, short: g, tier: GRADE_TIER[g], tip: lines(`Score ${s.rating} / 100`, rankText(percentileBelow(s.rating, sp.stage))) });
  const t = S.trainer;
  const need = creatureXpToNext(c.level);
  out.push({ key: 'level', label: 'Lv', value: String(c.level), short: String(c.level), tier: 'n', tip: lines(`XP ${full(c.xp)} / ${full(need)}`, t ? `Fights up to Lv ${t.level + LEVEL_SLACK}` : '') });
  const tb = talentBelow(c);
  out.push({ key: 'talent', label: 'Talent', value: `${s.iv}%`, short: `${s.iv}%`, tier: tierFor(tb), tip: lines(c.rolls.ivs.map((v, i) => `${SHORT[i]} ${v}`).join(' · '), rankText(tb)) });
  const qb = qualityBelow(c.rolls.quality);
  out.push({ key: 'quality', label: 'Quality', value: qualityEffect(c.rolls.quality), short: qualityEffect(c.rolls.quality), tier: tierFor(qb), tip: lines(`×${c.rolls.quality.toFixed(3)} every stat`, rankText(qb)) });
  const bl = CONFIG.bloodline[c.rolls.bloodline];
  const blTier: Tier[] = ['c', 'b', 'a', 's', 'sp'];
  out.push({ key: 'bloodline', label: 'Bloodline', value: bl.name, short: bl.name, tier: blTier[c.rolls.bloodline], tip: c.rolls.bloodline
    ? `${bl.mult >= 2 ? `×${bl.mult}` : `+${Math.round((bl.mult - 1) * 100)}%`} every stat · 1 in ${full(Math.round(100 / bl.pct))} catches`
    : `No bonus · ${bl.pct}% of catches` });
  if (c.rolls.aura) out.push({ key: 'aura', label: '', value: '✦ Aura', short: '✦', tier: 'gold', tip: `×${CONFIG.auraMult} every stat · 1 in ${full(c.boosted ? CONFIG.auraCatchOneIn / CONFIG.auraCharm.oddsMult : CONFIG.auraCatchOneIn)} catches` });
  const tr = traitOf(c);
  if (tr) out.push({ key: 'trait', label: '', value: `${tr.rare ? '◆ ' : ''}${esc(tr.name)}`, short: tr.rare ? '◆' : esc(tr.name), tier: tr.rare ? 's' : 'n', tip: lines(tr.desc, tr.rare ? `Rare · 1 in ${CONFIG.rareTraitOneIn} catches` : '') });
  const st = s.stats;
  out.push({ key: 'power', label: 'Power', value: full(s.power), short: full(s.power), tier: 'n', tip: [st.hp, st.might, st.guard, st.focus, st.will, st.swift].map((v, i) => `${SHORT[i]} ${full(v)}`).join(' · ') });
  return out;
}

/**
 * rollsOnly: just what was rolled at the catch (no level or power), as on the Records page.
 * noLevel: everything but the level, where the level shows beside the name already.
 */
export function rollPills(c: CreatureDTO, opts: { rollsOnly?: boolean; noLevel?: boolean } = {}): string {
  const out: string[] = [];
  for (const p of rollParts(c)) {
    if ((opts.rollsOnly || opts.noLevel) && p.key === 'level') continue;
    if (opts.rollsOnly && p.key === 'power') continue;
    if (p.key === 'power' && !opts.rollsOnly) { const rf = refinePill(c); if (rf) out.push(rf); const tp = trainPill(c); if (tp) out.push(tp); const md = medalPill(c); if (md) out.push(md); }
    out.push(pill(p.tier, p.label, p.value, p.tip, p.key));
  }
  return `<div class="rpills">${out.join('')}</div>`;
}

/**
 * The compact row of the team rail and the collection cards: the grade, then Talent, Quality, Bloodline (II and up)
 * and Aura as short colored pills. The full name and number of each is on hover, and the same rolls show with their
 * names on the lead card and the creature popup.
 */
export function miniPills(c: CreatureDTO): string {
  const out: string[] = [];
  for (const p of rollParts(c)) {
    if (p.key === 'level' || p.key === 'power' || (p.key === 'trait' && p.tier !== 's')) continue;
    if (p.key === 'bloodline' && !c.rolls.bloodline) continue;
    const name = p.key === 'grade' ? '' : p.key === 'trait' ? 'Rare trait' : p.key[0].toUpperCase() + p.key.slice(1);
    const tip = p.key === 'trait' ? lines(p.value.replace(/^◆ /, ''), p.tip) : lines(name ? `${name} ${p.short}` : `Grade ${p.short}`, p.tip);
    out.push(`<span class="rp m t-${p.tier}" data-k="${p.key}" tabindex="0" data-tip="${esc(tip)}" aria-label="${esc(name ? `${name} ${p.short}` : `Grade ${p.short}`)}">${p.short}</span>`);
  }
  return `<span class="rpills m">${out.join('')}</span>`;
}

/** "+N" for refine steps, beside the rolls but not one of them: it never moves the grade. Nothing when unrefined. */
export function refinePill(c: CreatureDTO): string {
  const r = refineOf(c), total = r.reduce((a, b) => a + b, 0);
  if (!total) return '';
  const parts = REFINE_STATS.filter((i) => r[i]).map((i) => `${STAT_NAMES[i]} +${r[i]}`).join(' · ');
  return pill('rf', 'Refine', `+${total}`, lines(parts, `${full(refineSpent(c))} star items used · not in the grade`));
}

/** "+N" for stone training steps (shared/train.ts): hunts and bosses only, never the grade. Nothing when untrained. */
export function trainPill(c: CreatureDTO): string {
  const r = trainOf(c), total = r.reduce((a, b) => a + b, 0);
  if (!total) return '';
  const parts = TRAIN_STATS.filter((i) => r[i]).map((i) => `${STAT_NAMES[i]} +${r[i]}`).join(' · ');
  return pill('tr', 'Trained', `+${total}`, lines(parts, `${full(trainSpent(c))} stones used · hunts and bosses only · not in the grade`));
}

/** War Medals: one pill with the war numbers. Nothing when the creature has none. */
export function medalPill(c: CreatureDTO): string {
  const m = c.medals ?? [];
  if (!m.length) return '';
  const nums = m.map((n) => `#${n}`).join(', ');
  return pill('medal', m.length > 1 ? `War Medals ×${m.length}` : 'War Medal', m.length > 1 ? `#${m[m.length - 1]}…` : `#${m[0]}`, `Top damage in Guild War ${nums}`);
}
